/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Row, Col, Label, FormGroup } from 'reactstrap';
import Select from 'react-select';
import { throttle, DebouncedFunc } from 'lodash';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import userApi from '../../../api/userApi';
import prototypeApi from '../../../api/prototypeApi';
import moodboardReferencesApi from '../../../api/moodboardReferencesApi';
import moodboardComponentTypeApi from '../../../api/moodboardComponentTypeApi';
import moodboardTagApi from '../../../api/moodboardTagApi';
import GridItem from '../components/GridItem';
import Pagination from '../../../components/common/Pagination';
// eslint-disable-next-line @typescript-eslint/camelcase
import { PAGE_LIMIT, PAGE_LIMIT_4K, BREAK_POINT_4k } from '../../../constants';
import useWindowSize from '../../../helpers/useWindowSizeHook';

const SweetAlert = withReactContent(Swal);
let filterThrottle: DebouncedFunc<any>;
const List = () => {
  const { deviceWidth } = useWindowSize();
  const [dataQuery, setDataQuery] = useState<{
    limit: number;
    page: number;
    componentTypeId: string | null;
    prototypeId: string | null;
    userId: string | null;
    type: string | null;
    tagId: string[] | null;
    sortByDate: string | null;
  }>({
    // eslint-disable-next-line @typescript-eslint/camelcase
    limit: deviceWidth > BREAK_POINT_4k ? PAGE_LIMIT_4K : PAGE_LIMIT,
    page: 1,
    componentTypeId: null,
    prototypeId: null,
    userId: null,
    type: null,
    tagId: [],
    sortByDate: '0',
  });
  const [users, setUsers] = useState<
    {
      _id: string;
      email: string;
      first_name: string;
      last_name: string;
      picture: string;
    }[]
  >([]);

  const [prototypeLists, setPrototypeLists] = useState<
    {
      _id: string;
      name: string;
    }[]
  >([]);

  const [componentTypeLists, setComponentTypeLists] = useState<
    {
      _id: string;
      name: string;
    }[]
  >([]);

  const [tagLists, setTagLists] = useState<
    {
      _id: string;
      name: string;
    }[]
  >([]);

  const [screens, setScreens] = useState<
    {
      _id: string;
      component: {
        _id: string;
        name: string;
      };
      prototype: {
        _id: string;
        name: string;
      };
      image_path: string;
      video_path: string;
      video_poster_path: string;
      mediaType: string;
      created_at: string;
      url: string;
      note: string;
      user: {
        _id: string;
        email: string;
        first_name: string;
        last_name: string;
        picture: string;
      };
      tags: [
        {
          _id: string;
          name: string;
        },
      ];
    }[]
  >([]);
  const [totalNumberOfScreens, setTotalNumberOfScreens] = useState(0);
  const [userRequestIsInProgress, setUserRequestIsInProgress] = useState(false);
  const [dataRequestIsInProgress, setDataRequestIsInProgress] = useState(true);

  const getData = useCallback(async () => {
    if (deviceWidth > 0) {
      if (filterThrottle) filterThrottle.cancel();
      filterThrottle = throttle(
        async () => {
          try {
            setDataRequestIsInProgress(true);
            let sortParam = null;
            if (dataQuery.sortByDate === '0') {
              sortParam = '-created_at'; // Latest first
            } else if (dataQuery.sortByDate === '1') {
              sortParam = 'created_at'; // Oldest first
            }
            const response = (
              await moodboardReferencesApi.getMoodboardReferencesList(
                dataQuery.limit,
                (dataQuery.page - 1) * dataQuery.limit,
                dataQuery.componentTypeId,
                dataQuery.prototypeId,
                dataQuery.userId,
                dataQuery.type,
                dataQuery.tagId,
                null,
                sortParam,
              )
            )?.data;
            const { data, count } = response;
            setScreens(data);
            setTotalNumberOfScreens(count);
            setDataRequestIsInProgress(false);
          } catch (error) {
            toast(
              'Error occurred while fetching data. Please reload to try again.',
              {
                type: 'error',
                autoClose: 10000,
              },
            );
          }
        },
        600,
        { leading: false, trailing: true },
      );
      filterThrottle();
    }
  }, [dataQuery, deviceWidth]);

  const getUsers = async () => {
    setUserRequestIsInProgress(true);
    const userData: {
      _id: string;
      email: string;
      first_name: string;
      last_name: string;
      picture: string;
    }[] = (await userApi.getUsersWithMoodboardReferences())?.data?.data || [];
    setUsers(userData);
    setUserRequestIsInProgress(false);
  };

  const getPrototypeList = async () => {
    try {
      const results = await prototypeApi.requestAllPrototypesList();
      setPrototypeLists(results.data.prototypeData);
    } catch (e) {
      toast('Error occurred while fetching data. Please reload to try again.', {
        type: 'error',
        autoClose: 10000,
      });
    }
  };

  const getComponentTypeList = async () => {
    try {
      const results =
        await moodboardComponentTypeApi.requestComponentTypeLists();
      setComponentTypeLists(results.data.componentData);
    } catch (e) {
      toast('Error occurred while fetching data. Please reload to try again.', {
        type: 'error',
        autoClose: 10000,
      });
    }
  };

  const getTagList = async () => {
    try {
      const results = await moodboardTagApi.requestTagLists();
      setTagLists(results.data.tags);
    } catch (e) {
      toast('Error occurred while fetching data. Please reload to try again.', {
        type: 'error',
        autoClose: 10000,
      });
    }
  };

  useEffect(() => {
    getUsers();
    getData();
    getPrototypeList();
    getComponentTypeList();
    getTagList();
  }, [getData]);

  useEffect(() => {
    getData();
  }, [dataQuery, getData, deviceWidth]);

  const onPageChange = ({ selected: selectedPage }: { selected: number }) => {
    setDataQuery({ ...dataQuery, page: selectedPage + 1 });
  };

  const onLimitChange = ({ target }: { target: HTMLSelectElement }) => {
    setDataQuery({ ...dataQuery, page: 1, limit: parseInt(target.value, 10) });
  };

  const handleItemDelete = async (id: string) => {
    SweetAlert.fire({
      title: 'Confirm Action',
      text: 'Are you sure you want to delete this item? This action is irreversible.',
      icon: 'question',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await moodboardReferencesApi.deleteById(id);
        toast('Successfully deleted.', {
          type: 'success',
        });
        getData();
        setDataQuery({
          ...dataQuery,
          page: 1,
        });
      }
    });
  };

  const handleToggleLegacy = async (id: string, isLegacy: boolean) => {
    try {
      await moodboardReferencesApi.toggleLegacyStatus(id, isLegacy);

      toast('Legacy status updated successfully', {
        type: 'success',
        autoClose: 3000,
      });
    } catch (error) {
      toast('Error updating legacy status', {
        type: 'error',
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    if (deviceWidth > 0) {
      setDataQuery({
        ...dataQuery,
        // eslint-disable-next-line @typescript-eslint/camelcase
        limit: deviceWidth > BREAK_POINT_4k ? PAGE_LIMIT_4K : PAGE_LIMIT,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceWidth]);

  const items =
    screens.length > 0 ? (
      screens.map((screen) => (
        <GridItem
          // eslint-disable-next-line no-underscore-dangle
          itemKey={screen._id}
          screen={screen}
          onDelete={handleItemDelete}
          onToggleLegacy={handleToggleLegacy}
        />
      ))
    ) : (
      <span className="mx-auto py-3">
        Sorry, Your filter produced no results!
      </span>
    );

  const userOptions: { value: string | null; label: string }[] = [
    { value: null, label: 'All' },
    ...users.map((user) => ({
      // eslint-disable-next-line no-underscore-dangle
      value: user._id,
      label: `${user.first_name} ${user.last_name}`,
    })),
  ];
  const selectedUser = userOptions.find(
    ({ value }) => value === dataQuery.userId,
  );

  const prototypeOptions: { value: string | null; label: string }[] = [
    { value: null, label: 'All' },
    { value: '0', label: 'Not Specified' },
    ...prototypeLists.map((prototype) => ({
      // eslint-disable-next-line no-underscore-dangle
      value: prototype._id,
      label: prototype.name,
    })),
  ];

  const selectedPrototypeOption = prototypeOptions.find(
    ({ value }) => value === dataQuery.prototypeId,
  );

  const componentTypeOptions: { value: string | null; label: string }[] = [
    { value: null, label: 'All' },
    ...componentTypeLists.map((component) => ({
      // eslint-disable-next-line no-underscore-dangle
      value: component._id,
      label: component.name,
    })),
  ];

  const selectedComponentTypeOption = componentTypeOptions.find(
    ({ value }) => value === dataQuery.componentTypeId,
  );

  const tagOptions: { value: string | null; label: string }[] = [
    ...tagLists.map((component) => ({
      // eslint-disable-next-line no-underscore-dangle
      value: component._id,
      label: component.name,
    })),
  ];
  const sortByDateOptions: { value: string | null; label: string }[] = [
    { value: '0', label: 'Latest' },
    { value: '1', label: 'Oldest' },
  ];

  const selectedSortByDateOption = sortByDateOptions.find(
    ({ value }) => value === dataQuery.sortByDate,
  );

  const selectedTagOption = tagOptions.find(
    ({ value }) => value === dataQuery.tagId,
  );

  return (
    <div className="ar-common-content">
      <div className="ar-common-search-panel-wrapper">
        <Row>
          <Col>
            <div className="form ar-common-form ar-common-search-panel ar-common-search-panel--reference ">
              <div className="ar-common-form__group">
                <FormGroup className="d-block">
                  <Label className="mb-2">Filter by submitted user</Label>
                  <Select
                    classNamePrefix="rctslct"
                    value={selectedUser}
                    options={userOptions}
                    onChange={(selectedOption) =>
                      setDataQuery({
                        ...dataQuery,
                        page: 1,
                        userId: selectedOption?.value || null,
                      })
                    }
                    isLoading={userRequestIsInProgress}
                    isDisabled={userRequestIsInProgress}
                  />
                </FormGroup>
              </div>
              <div className="ar-common-form__group">
                <FormGroup className="d-block">
                  <Label className="mb-2">Filter by prototype</Label>
                  <Select
                    classNamePrefix="rctslct"
                    value={selectedPrototypeOption}
                    options={prototypeOptions}
                    onChange={(selectedProject) =>
                      setDataQuery({
                        ...dataQuery,
                        page: 1,
                        prototypeId: selectedProject?.value || null,
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="ar-common-form__group">
                <FormGroup className="d-block">
                  <Label className="mb-2">Filter by section</Label>
                  <Select
                    classNamePrefix="rctslct"
                    value={selectedComponentTypeOption}
                    options={componentTypeOptions}
                    onChange={(selectedComponentType) =>
                      setDataQuery({
                        ...dataQuery,
                        page: 1,
                        componentTypeId: selectedComponentType?.value || null,
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="ar-common-form__group">
                <FormGroup className="d-block">
                  <Label className="mb-2">Filter by tag</Label>
                  <Select
                    isMulti
                    classNamePrefix="rctslct"
                    options={tagOptions}
                    onChange={(selectedTag) => {
                      const selectedOptions: any = [];
                      // eslint-disable-next-line no-unused-expressions
                      selectedTag?.forEach((selectedTagitem) => {
                        selectedOptions.push(selectedTagitem.value);
                      });
                      setDataQuery({
                        ...dataQuery,
                        page: 1,
                        tagId: selectedOptions || null,
                      });
                    }}
                  />
                </FormGroup>
              </div>
              <div className="ar-common-form__group">
                <FormGroup className="d-block">
                  <Label className="mb-2">Sort by</Label>
                  <Select
                    classNamePrefix="rctslct"
                    value={selectedSortByDateOption}
                    options={sortByDateOptions}
                    onChange={(selectedOption) =>
                      setDataQuery({
                        ...dataQuery,
                        page: 1,
                        sortByDate: selectedOption?.value || '0',
                      })
                    }
                  />
                </FormGroup>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col>
          {dataRequestIsInProgress ? (
            <div style={{ minHeight: 250 }}>
              <div className="loading-indicator" />
            </div>
          ) : (
            // eslint-disable prettier/prettier
            <div className="ar-common-content__inner">
              <div className="ar-common-posts">
                <div
                  className={`ar-common-posts__list
                  ${screens.length === 0 ? 'd-block' : ''}`}
                >
                  {items}
                </div>
              </div>
            </div>
          )}
          {!dataRequestIsInProgress &&
            screens.length > 0 &&
            deviceWidth > 0 && (
              <Pagination
                count={totalNumberOfScreens}
                pageCount={Math.ceil(totalNumberOfScreens / dataQuery.limit)}
                onPageChange={onPageChange}
                onPageSelect={onLimitChange}
                currentPage={dataQuery.page}
                pageLimit={dataQuery.limit}
              />
            )}
        </Col>
      </Row>
    </div>
  );
};

export default List;
